import { DESIGN_GENERATOR_VERSION, SHAPES, upgradeExitRules, validateDesign, validateLevel, type LevelDesign } from './generator';
import type { Level } from './types';
import { validateShapeGroups, type ShapeGroup } from './shapeGroups';
import { FIRST_CUSTOM_LEVEL } from './openingLevels';

export type Draft = { level: Level; design: LevelDesign; updatedAt: string };
type ArchivedLevel = { level: Level; design?: LevelDesign };
export type Release = { version: number; createdAt: string; levels: Record<string, Level>; designs?: Record<string, LevelDesign>; note: string; overflow?: Record<string, ArchivedLevel> };
export type Catalog = { schema: 1; drafts: Record<string, Draft>; releases: Release[]; activeVersion: number | null; shapeGroups?: ShapeGroup[]; overflowDrafts?: Record<string, Draft> };
export const emptyCatalog = (): Catalog => ({ schema: 1, drafts: {}, releases: [], activeVersion: null });

// Adapt old snapshots in memory. The original stored catalog is retained until
// the next explicit catalog save; exports and play use the upgraded content.
export function upgradeCatalogContent(catalog: Catalog): void {
  const upgrade = (level: Level) => {
    if (level.generatorVersion !== 4) return level;
    assertPublishable(level, true);
    return upgradeExitRules(level);
  };
  for (const draft of Object.values(catalog.drafts)) draft.level = upgrade(draft.level);
  for (const release of catalog.releases) {
    for (const [id, level] of Object.entries(release.levels)) release.levels[id] = upgrade(level);
  }
}

/** Move legacy custom slots after FTUE, using one mapping for drafts and every
 * release so rollback and edits still refer to the same boards. Read-only:
 * originals remain in storage until the next explicit save. */
export function reserveFtueSlots(catalog: Catalog): void {
  const numbers = new Set<number>();
  const collect = (id: string, level: Level, design?: LevelDesign) => {
    if (!Number.isInteger(level.number) || level.number < 1 || level.number > 100 || id !== level.id || id !== `level-${level.number}`) throw new Error('Level slot does not match its identity.');
    if (design && design.number !== level.number) throw new Error('Recipe does not match its board.');
    numbers.add(level.number);
  };
  for (const [id, draft] of Object.entries(catalog.drafts)) collect(id, draft.level, draft.design);
  for (const release of catalog.releases) {
    for (const [id, level] of Object.entries(release.levels)) collect(id, level, release.designs?.[id]);
    if (Object.keys(release.designs ?? {}).some(id => !release.levels[id])) throw new Error('Recipe is missing its level.');
  }
  if (![...numbers].some(number => number < FIRST_CUSTOM_LEVEL)) return;
  let next = FIRST_CUSTOM_LEVEL;
  const mapping = new Map([...numbers].sort((a, b) => a - b).map(number => {
    const target = Math.max(next, number);
    next = target + 1;
    return [number, target];
  }));
  const move = (level: Level): Level => {
    const number = mapping.get(level.number)!;
    return { ...level, number, id: `level-${number}` };
  };
  const drafts: Catalog['drafts'] = {};
  for (const [id, draft] of Object.entries(catalog.drafts)) {
    const level = move(draft.level);
    if (level.number > 100) {
      catalog.overflowDrafts ??= {};
      if (catalog.overflowDrafts[id]) throw new Error('An archived draft already uses this slot. Export the catalog before migrating.');
      catalog.overflowDrafts[id] = draft;
    } else drafts[level.id] = { ...draft, level, design: { ...draft.design, number: level.number } };
  }
  catalog.drafts = drafts;
  for (const release of catalog.releases) {
    const levels: Release['levels'] = {};
    const designs: NonNullable<Release['designs']> = {};
    for (const [id, original] of Object.entries(release.levels)) {
      const level = move(original);
      const design = release.designs?.[id];
      if (level.number > 100) {
        release.overflow ??= {};
        if (release.overflow[id]) throw new Error('An archived level already uses this slot. Export the catalog before migrating.');
        release.overflow[id] = { level: original, ...(design ? { design } : {}) };
      } else {
        levels[level.id] = level;
        if (design) designs[level.id] = { ...design, number: level.number };
      }
    }
    release.levels = levels;
    if (release.designs) release.designs = designs;
  }
}

export function assertPublishable(level: Level, historicalShapeExits = false): void {
  if (![4, DESIGN_GENERATOR_VERSION].includes(level.generatorVersion) || !Number.isInteger(level.number) || level.number < 1 || level.number > 100 || level.id !== `level-${level.number}`) throw new Error('Invalid level identity or generator version.');
  if (!Number.isInteger(level.width) || !Number.isInteger(level.height) || level.width < 8 || level.width > 32 || level.height < 8 || level.height > 40) throw new Error('Invalid board dimensions.');
  if (!Number.isInteger(level.timeLimit) || level.timeLimit < 15 || level.timeLimit > 300) throw new Error('Timer must be between 15 and 300 seconds.');
  if (!Number.isInteger(level.difficulty) || level.difficulty < 1 || level.difficulty > 5) throw new Error('Invalid difficulty tier.');
  if (!SHAPES.includes(level.shape) || (level.shape === 'square' && level.width !== level.height)) throw new Error('Invalid board shape.');
  if (!Number.isInteger(level.seed) || level.seed < 0 || level.seed > 4294967295) throw new Error('Invalid level seed.');
  if (!Array.isArray(level.active) || level.active.length > 1280 || !Array.isArray(level.arrows) || level.arrows.length > 640) throw new Error('Board data is too large.');
  if (level.arrows.some(a => typeof a.id !== 'string' || !a.id || a.id.length > 64 || !Array.isArray(a.cells) || a.cells.length < 2 || a.cells.length > 1280)) throw new Error('Invalid arrow data.');
  if (level.arrows.some(a => a.cells.some(c => !Number.isInteger(c.x) || !Number.isInteger(c.y)))) throw new Error('Arrow coordinates must be integers.');
  if (new Set(level.active.map(c => `${c.x},${c.y}`)).size !== level.active.length || !level.active.length || level.active.some(c => !Number.isInteger(c.x) || !Number.isInteger(c.y) || c.x < 0 || c.y < 0 || c.x >= level.width || c.y >= level.height)) throw new Error('Invalid active-cell mask.');
  if (new Set(level.arrows.map(a => a.id)).size !== level.arrows.length) throw new Error('Arrow IDs must be unique.');
  if (level.arrows.some(a => !['UP', 'DOWN', 'LEFT', 'RIGHT'].includes(a.direction))) throw new Error('Invalid arrow direction.');
  const issues = validateLevel(level, historicalShapeExits ? 'shape-edge' : 'board-edge');
  if (issues.length) throw new Error(issues[0]);
  if (level.arrows.reduce((sum, a) => sum + a.cells.length, 0) !== level.active.length) throw new Error('The board must be completely filled.');
}
export function activeRelease(catalog: Catalog): Release | undefined {
  return catalog.releases.find(r => r.version === catalog.activeVersion);
}
/** The published board for slot `number`, or null when the catalog has none (the built-in level is used). */
export function publishedLevel(catalog: Catalog | undefined, number: number): Level | null {
  if (!catalog || number < FIRST_CUSTOM_LEVEL) return null;
  try {
    const level = activeRelease(catalog)?.levels[`level-${number}`];
    if (!level) return null;
    assertPublishable(level);
    return structuredClone(level);
  } catch { return null; } // Corrupt/unavailable custom content falls back to the built-in catalog.
}

/** Validate an exported catalog completely before offering an atomic local restore. */
export function parseCatalogBackup(text: string): Catalog {
  if (text.length > 10_000_000) throw new Error('Choose a catalog smaller than 10 MB.');
  const value = JSON.parse(text) as Catalog;
  if (!value || value.schema !== 1 || !value.drafts || typeof value.drafts !== 'object' || Array.isArray(value.drafts) || !Array.isArray(value.releases) || value.releases.length > 3 || Object.keys(value.drafts).length > 100) throw new Error('Unsupported catalog backup.');
  if (value.shapeGroups !== undefined) validateShapeGroups(value.shapeGroups);
  upgradeCatalogContent(value);
  const checkLevels = (levels: Record<string, Level>) => {
    if (!levels || typeof levels !== 'object' || Array.isArray(levels) || Object.keys(levels).length > 100) throw new Error('Invalid catalog levels.');
    for (const [id, level] of Object.entries(levels)) {
      assertPublishable(level);
      if (id !== level.id) throw new Error('Level slot does not match its identity.');
    }
  };
  const checkDesign = (design: LevelDesign, level: Level) => {
    validateDesign(design);
    for (const key of ['number', 'seed', 'shape', 'width', 'height', 'difficulty', 'timeLimit'] as const) {
      if (design[key] !== level[key]) throw new Error('Recipe does not match its board.');
    }
  };
  const dateValid = (date: string) => typeof date === 'string' && Number.isFinite(Date.parse(date));
  const checkArchive = (archive: Record<string, ArchivedLevel>) => {
    if (!archive || typeof archive !== 'object' || Array.isArray(archive) || Object.keys(archive).length > 100) throw new Error('Invalid archived levels.');
    checkLevels(Object.fromEntries(Object.entries(archive).map(([id, item]) => [id, item.level])));
    for (const item of Object.values(archive)) if (item.design) checkDesign(item.design, item.level);
  };
  if (value.overflowDrafts !== undefined) {
    checkArchive(value.overflowDrafts);
    for (const draft of Object.values(value.overflowDrafts)) {
      checkDesign(draft.design, draft.level);
      if (!dateValid(draft.updatedAt)) throw new Error('Invalid draft timestamp.');
    }
  }
  checkLevels(Object.fromEntries(Object.entries(value.drafts).map(([id, d]) => [id, d.level])));
  for (const draft of Object.values(value.drafts)) {
    checkDesign(draft.design, draft.level);
    if (!dateValid(draft.updatedAt)) throw new Error('Invalid draft timestamp.');
  }
  const versions = new Set<number>();
  for (const release of value.releases) {
    if (!Number.isSafeInteger(release.version) || release.version < 1 || versions.has(release.version) || !dateValid(release.createdAt)) throw new Error('Invalid release history.');
    versions.add(release.version);
    checkLevels(release.levels);
    if (release.overflow !== undefined) checkArchive(release.overflow);
    if (release.designs !== undefined) {
      if (!release.designs || typeof release.designs !== 'object' || Array.isArray(release.designs)) throw new Error('Invalid release recipes.');
      for (const [id, design] of Object.entries(release.designs)) {
        if (!release.levels[id]) throw new Error('Recipe is missing its level.');
        checkDesign(design, release.levels[id]);
      }
    }
  }
  if (value.activeVersion !== null && !versions.has(value.activeVersion)) throw new Error('Active release is missing from this backup.');
  reserveFtueSlots(value);
  return value;
}
